import React, { useState } from 'react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Upload, Calendar, Users, Heart } from 'lucide-react';
import { schoolInfo } from '../data/mock';

const Gallery = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Gallery with all your beautiful Annual Day photos
  const galleryImages = [
    {
      id: 1,
      src: "https://customer-assets.emergentagent.com/job_sur-sangeet/artifacts/g67sea5j_DSC_8623.jpg",
      title: "Student Performance",
      category: "performance",
      year: "2024"
    },
    {
      id: 2,
      src: "https://customer-assets.emergentagent.com/job_sur-sangeet/artifacts/7dqggksw_DSC_8607.jpg",
      title: "Classical Music Class",
      category: "classes",
      year: "2024"
    },
    {
      id: 3,
      src: "https://customer-assets.emergentagent.com/job_sur-sangeet/artifacts/9okt2kga_annual%20day%202025.png",
      title: "Annual Day 2025 Collage",
      category: "annual-day",
      year: "2025"
    },
    {
      id: 4,
      src: "https://customer-assets.emergentagent.com/job_sur-sangeet/artifacts/9rtqeveo_DSC_8472.jpeg",
      title: "Students Ready to Perform",
      category: "annual-day",
      year: "2025"
    },
    {
      id: 5,
      src: "https://customer-assets.emergentagent.com/job_sur-sangeet/artifacts/t6eqzrxn_DSC_8509.jpeg",
      title: "Annual Day Audience",
      category: "annual-day",
      year: "2025"
    },
    {
      id: 6,
      src: "https://customer-assets.emergentagent.com/job_sur-sangeet/artifacts/d2x7txdr_DSC_8511.jpeg",
      title: "Students & Teacher Group Photo",
      category: "annual-day",
      year: "2025"
    },
    {
      id: 7,
      src: "https://customer-assets.emergentagent.com/job_sur-sangeet/artifacts/wrhi9i5t_DSC_8946.jpeg",
      title: "Family at Annual Day",
      category: "annual-day",
      year: "2025"
    },
    {
      id: 8,
      src: "https://customer-assets.emergentagent.com/job_sur-sangeet/artifacts/5emxfre5_DSC_8765.jpeg",
      title: "Community Gathering",
      category: "annual-day",
      year: "2025"
    },
    {
      id: 9,
      src: "https://customer-assets.emergentagent.com/job_sur-sangeet/artifacts/x3f66z2h_DSC_8634.jpeg",
      title: "Young Performers",
      category: "performance",
      year: "2025"
    },
    {
      id: 10,
      src: "https://customer-assets.emergentagent.com/job_sur-sangeet/artifacts/gy1ok4xf_DSC_8873.jpeg",
      title: "Musical Performance",
      category: "performance",
      year: "2025"
    },
    {
      id: 11,
      src: "https://customer-assets.emergentagent.com/job_sur-sangeet/artifacts/66olajyx_DSC_8459.jpeg",
      title: "Concert Moment",
      category: "performance",
      year: "2025"
    },
    {
      id: 12,
      src: "https://customer-assets.emergentagent.com/job_sur-sangeet/artifacts/d1dveot3_DSC_8885%20%281%29.jpeg",
      title: "Cultural Celebration",
      category: "annual-day",
      year: "2025"
    }
  ];

  const categories = [
    { id: 'all', label: 'All Photos', icon: Heart },
    { id: 'annual-day', label: 'Annual Day', icon: Calendar },
    { id: 'performance', label: 'Performances', icon: Users },
    { id: 'classes', label: 'Classes', icon: Upload }
  ];

  const filteredImages = selectedCategory === 'all' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === selectedCategory);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-sky-50 to-indigo-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4 bg-blue-100 text-blue-800">
              📸 Our Memories
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Gallery
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Capturing the beautiful moments of our musical journey - from classes to performances to our special Annual Day celebrations
            </p>
          </div>
        </div>
      </section>

      {/* Removed upload instructions section */}

      {/* Category Filter */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => {
              const IconComponent = category.icon;
              return (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.id ? "default" : "outline"}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-2 ${
                    selectedCategory === category.id 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'border-blue-300 text-blue-700 hover:bg-blue-50'
                  }`}
                >
                  <IconComponent className="h-4 w-4" />
                  {category.label}
                </Button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredImages.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredImages.map((image) => (
                <Card key={image.id} className="group hover:shadow-xl transition-all duration-300 overflow-hidden border-blue-100">
                  <CardContent className="p-0">
                    <div className="relative">
                      <img
                        src={image.src}
                        alt={image.title}
                        className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <h3 className="text-lg font-semibold">{image.title}</h3>
                        <p className="text-sm text-blue-200">{image.year}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">No photos in this category yet.</p>
              <p className="text-sm text-gray-500 mt-2">More photos coming soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* Social Media Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Follow Us for More Updates!
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Stay connected and see more of our musical journey on social media
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              asChild
              className="bg-red-600 hover:bg-red-700 text-white shadow-lg"
            >
              <a href={schoolInfo.socialMedia?.youtube || "#"} target="_blank" rel="noopener noreferrer">
                📺 YouTube Channel
              </a>
            </Button>
            <Button 
              size="lg"
              asChild
              className="bg-pink-600 hover:bg-pink-700 text-white shadow-lg"
            >
              <a href={schoolInfo.socialMedia?.instagram || "#"} target="_blank" rel="noopener noreferrer">
                📸 Instagram
              </a>
            </Button>
          </div>
          <p className="text-sm text-blue-200 mt-4">
            Watch our videos and see behind-the-scenes moments from our classes and performances!
          </p>
        </div>
      </section>
    </div>
  );
};

export default Gallery;